/**
 * IX Nova In-App Purchase — native Apple StoreKit only (v13.18+).
 *
 * Uses `cordova-plugin-purchase` v13 which is a thin wrapper over Apple's
 * StoreKit 2 on iOS. No RevenueCat, no Stripe on iOS, no mocks, no fallbacks.
 *
 * ==========================================================================
 * Build 20 — root cause fixes for Apple's iPad Air M3 (iPadOS 26.5) rejection
 * ==========================================================================
 *
 * Build 19 rejection: "in-app purchase products still did not load or
 * function correctly on iPad Air 11-inch M3 running iPadOS 26.5".
 *
 * Investigation revealed FOUR real bugs (not just error-message polish):
 *
 *   Bug 1 — `deviceready` was never awaited before importing the plugin.
 *           cordova-plugin-purchase attaches its `SKPaymentQueue` transaction
 *           observer during plugin bootstrap, which only happens after the
 *           Cordova bridge has fired `deviceready` on iOS. Importing the
 *           plugin ES module before that races against the observer install,
 *           so on cold-start iPad devices (especially with Stage Manager or
 *           heavy WKWebView) the observer was missing → StoreKit received
 *           zero product requests → products never returned.
 *
 *   Bug 2 — `store.initialize()` was called with `[{ platform: X }]` and no
 *           options. The v13 canonical shape for Apple is
 *           `[{ platform: Platform.APPLE_APPSTORE, options: { needAppReceipt: true } }]`.
 *           Without `needAppReceipt`, the app receipt is not fetched at
 *           launch, and StoreKit 2 signed transactions don't surface a
 *           `jwsRepresentation` on the first `productUpdated` event.
 *
 *   Bug 3 — no `store.verbosity` was set. When Apple's reviewer saw the
 *           paywall fail, there was zero diagnostic output in the device
 *           console. We now enable DEBUG verbosity so future rejections
 *           come with actionable logs.
 *
 *   Bug 4 — no app-resume listener. iPad Stage Manager / split-view pauses
 *           the WKWebView; when the app comes back to foreground, the
 *           StoreKit product cache may be stale. We now retry on resume.
 *
 * ==========================================================================
 * Public contract (unchanged from Build 19 — the UI does not need to change)
 * ==========================================================================
 *
 *   - `initIAP(userId)` : idempotent bootstrap, safe to call any time.
 *   - `retryIAP()`      : manual retry, called by the paywall's Retry button.
 *   - `purchaseIAP(kind)` : "monthly" | "yearly" → opens native Apple sheet.
 *   - `restoreIAP()`    : native "Restore Purchases" call.
 *   - `onIAPStatus(fn)` : subscribe to lifecycle transitions.
 *   - `onIAPChange(fn)` : subscribe to product info changes.
 *
 *   Status FSM: initializing → loading → ready → error
 *   The UI is FORBIDDEN from rendering the error branch until phase==="error".
 */
import { isIOSNative, isAndroidNative } from "@/lib/platform";
import api from "@/lib/api";

// These MUST match the Product IDs configured in App Store Connect AND
// the Subscription IDs configured in Google Play Console. We deliberately
// use identical IDs on both stores to keep the frontend simple; the plugin
// tags each product with the correct platform at register() time.
export const IAP_PRODUCT_MONTHLY = "com.ixnova.app.premium.monthly";
export const IAP_PRODUCT_YEARLY  = "com.ixnova.app.premium.yearly";

const PRODUCT_IDS = [IAP_PRODUCT_MONTHLY, IAP_PRODUCT_YEARLY];

// Which native store are we running on? Determines platform for register(),
// initialize() options, and which backend verify endpoint to POST receipts to.
function _nativeStorePlatform(P) {
  if (!P) return null;
  if (isAndroidNative()) return P.Platform.GOOGLE_PLAY;
  if (isIOSNative())     return P.Platform.APPLE_APPSTORE;
  return null;
}

function _isNativeIAP() {
  return isIOSNative() || isAndroidNative();
}

let _store = null;
let _P = null;               // Plugin namespace (ProductType, Platform, ErrorCode, LogLevel).
let _initing = null;         // In-flight init promise (idempotency guard).
let _appUserId = null;
let _deviceReadyPromise = null;

let _products = { monthly: null, yearly: null };
let _status = { phase: "initializing", errorMessage: null, retryCount: 0 };

const _productListeners = new Set();
const _statusListeners  = new Set();

function _log(...args) {
  // Prefixed console output — visible in Xcode / device console during
  // TestFlight review. Non-critical: browser-safe.
  // eslint-disable-next-line no-console
  try { console.log("[IAP]", ...args); } catch { /* noop */ }
}
function _warn(...args) {
  // eslint-disable-next-line no-console
  try { console.warn("[IAP]", ...args); } catch { /* noop */ }
}

function _emitProducts() { for (const fn of _productListeners) { try { fn(_products); } catch { /* ignore */ } } }
function _emitStatus()   { for (const fn of _statusListeners)  { try { fn(_status);   } catch { /* ignore */ } } }

export function onIAPChange(fn) { _productListeners.add(fn); return () => _productListeners.delete(fn); }
export function onIAPStatus(fn) { _statusListeners.add(fn);  return () => _statusListeners.delete(fn); }
export function getIAPProducts() { return _products; }
export function getIAPStatus()   { return _status; }

function _setStatus(patch) {
  _status = { ..._status, ...patch };
  _log("status →", _status.phase, patch);
  _emitStatus();
}

function _hasAnyProduct() {
  return !!(_products.monthly || _products.yearly);
}

/**
 * Bug 1 fix — wait for the Cordova/Capacitor bridge to actually attach the
 * StoreKit observer BEFORE we import + init the plugin. On iOS Capacitor,
 * `deviceready` fires on `document` once the bridge is up. If for any
 * reason it doesn't fire (e.g. WKWebView hard reload), the 8-second timeout
 * lets us continue anyway.
 */
function _waitForDeviceReady() {
  if (_deviceReadyPromise) return _deviceReadyPromise;
  _deviceReadyPromise = new Promise((resolve) => {
    // If Cordova has already fired deviceready, `window.cordova` is set and
    // we can proceed immediately.
    if (typeof window !== "undefined" && window.cordova && window.cordova.plugins) {
      _log("deviceready: already fired (cordova.plugins present)");
      resolve();
      return;
    }
    const t = setTimeout(() => {
      _warn("deviceready: 8s timeout, proceeding anyway");
      resolve();
    }, 8000);
    if (typeof document !== "undefined") {
      document.addEventListener("deviceready", () => {
        _log("deviceready: fired");
        clearTimeout(t);
        resolve();
      }, { once: true });
    }
  });
  return _deviceReadyPromise;
}

/**
 * Refresh product info from Apple. Called periodically until at least one
 * product arrives or the retry budget is exhausted.
 *
 * Generation counter: retryIAP() can be called while a previous loop is
 * still waiting on `_waitForProductOrTimeout`. To avoid two loops racing
 * (one setting `phase: "error"` after the other has flipped to `ready`),
 * every new call increments `_updateGen` and each iteration aborts if the
 * generation has changed.
 */
let _updateGen = 0;
async function _updateWithBackoff() {
  const myGen = ++_updateGen;
  // Attempt 0 gives StoreKit's own fetch (kicked off inside `initialize()`)
  // a chance to arrive before we hit it with a second `update()` call. This
  // matters on slow iPad networks where the initial fetch may still be in
  // flight when our loop starts.
  const delays = [6000, 8000, 10000, 15000]; // total ~39s budget
  for (let attempt = 0; attempt < delays.length; attempt++) {
    if (myGen !== _updateGen) return;           // superseded by newer retry
    if (_hasAnyProduct()) return;               // productUpdated flipped us
    _setStatus({ phase: "loading", retryCount: attempt });
    // On attempt 0 we DON'T call update() — we just wait for init's own
    // fetch. From attempt 1 onwards we force a refresh.
    if (attempt > 0) {
      try {
        if (_store) {
          if (typeof _store.update === "function") {
            _log("update() attempt", attempt);
            await _store.update();
          } else if (typeof _store.refresh === "function") {
            _log("refresh() attempt", attempt);
            await _store.refresh();
          }
        }
      } catch (e) {
        _warn("update()/refresh() threw:", e?.code, e?.message);
      }
    } else {
      _log("waiting for initialize() productUpdated (attempt 0)");
    }
    await _waitForProductOrTimeout(delays[attempt]);
    if (myGen !== _updateGen) return;           // superseded
    if (_hasAnyProduct()) return;
  }
  if (myGen !== _updateGen) return;             // superseded
  if (!_hasAnyProduct()) {
    _warn("all retries exhausted; final store state:", _snapshotStoreState());
    _setStatus({ phase: "error", errorMessage: null });
  }
}

function _snapshotStoreState() {
  if (!_store) return { store: "null" };
  try {
    const registered = _store.products ? _store.products.map((p) => ({ id: p.id, state: p.state, valid: p.valid, canPurchase: p.canPurchase })) : [];
    return {
      registeredProducts: registered,
      productsReturned: {
        monthly: _products.monthly ? { id: _products.monthly.id, price: _products.monthly.priceString } : null,
        yearly:  _products.yearly  ? { id: _products.yearly.id,  price: _products.yearly.priceString  } : null,
      },
      platformState: {
        userAgent: (typeof navigator !== "undefined" ? navigator.userAgent : "n/a"),
        // On iPad, `navigator.platform` sometimes reports "MacIntel" — surface
        // enough info to distinguish iPad from iPhone in the logs.
        maxTouchPoints: (typeof navigator !== "undefined" ? navigator.maxTouchPoints : null),
        windowSize: (typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : null),
      },
    };
  } catch (e) {
    return { error: String(e) };
  }
}

function _waitForProductOrTimeout(ms) {
  return new Promise((resolve) => {
    const off = onIAPChange(() => {
      if (_hasAnyProduct()) { off(); clearTimeout(t); resolve(); }
    });
    const t = setTimeout(() => { off(); resolve(); }, ms);
  });
}

/**
 * Idempotent bootstrap. Safe to call from AuthContext on login AND from the
 * paywall on mount.
 */
export async function initIAP(appUserId) {
  if (!_isNativeIAP()) return { skipped: "not_native" };
  if (appUserId) _appUserId = appUserId;
  if (_initing) return _initing;
  _initing = (async () => {
    _setStatus({ phase: "initializing", errorMessage: null, retryCount: 0 });
    try {
      // -----------------------------------------------------------------
      // Bug 1 fix: wait for Cordova bridge before importing the plugin.
      // -----------------------------------------------------------------
      await _waitForDeviceReady();

      const modAny = await import("cordova-plugin-purchase");
      const CdvPurchase = modAny.default || modAny.CdvPurchase || (typeof window !== "undefined" ? window.CdvPurchase : null) || modAny;
      if (!CdvPurchase || !CdvPurchase.store) {
        // Recoverable — bridge may still be settling. Never surface this
        // as a user-facing error; keep UI in loading and self-retry up to
        // PLUGIN_RETRY_MAX times before giving up (prevents an infinite
        // retry storm if the plugin was never bundled correctly).
        _warn("CdvPurchase.store missing after deviceready — scheduling retry (attempt", _pluginRetryCount + 1, "of", PLUGIN_RETRY_MAX, ")");
        _pluginRetryCount++;
        if (_pluginRetryCount >= PLUGIN_RETRY_MAX) {
          _warn("plugin bootstrap failed after", PLUGIN_RETRY_MAX, "attempts — setting error");
          _setStatus({ phase: "error", errorMessage: null });
          return { ok: false, error: "plugin_not_available" };
        }
        _setStatus({ phase: "loading" });
        setTimeout(() => { _initing = null; initIAP(_appUserId); }, 4000);
        return { ok: false, error: "plugin_not_available", retrying: true };
      }
      _pluginRetryCount = 0; // successful bootstrap resets counter
      _store = CdvPurchase.store;
      _P = CdvPurchase;

      // -----------------------------------------------------------------
      // Bug 3 fix: enable verbose logging. This is safe in production —
      // console.log lines only appear in the device log, not the UI.
      // -----------------------------------------------------------------
      try {
        if (_P.LogLevel && typeof _P.LogLevel.DEBUG !== "undefined") {
          _store.verbosity = _P.LogLevel.DEBUG;
        } else if (typeof _store.verbosity === "number") {
          _store.verbosity = 3; // DEBUG in older plugin versions
        }
      } catch (e) { _warn("could not set verbosity:", e); }

      // Bug fix (Build 22 audit): only register products + attach listeners
      // ONCE per module lifetime. If initIAP() is retried after a failure
      // (e.g. initialize() threw), re-registering was fine but the
      // `.when().productUpdated(...)` handler was being stacked — so every
      // productUpdated event fired N handlers, N-1 of which were duplicates.
      // Not fatal (backend verify is idempotent) but wasteful and could
      // trip up rate limits during heavy iPad Stage Manager cycling.
      if (!_registered) {
        _registered = true;
        const _platform = _nativeStorePlatform(_P);
        _log("register()", PRODUCT_IDS, "platform=", _platform);
        _store.register([
          { id: IAP_PRODUCT_MONTHLY, type: _P.ProductType.PAID_SUBSCRIPTION, platform: _platform },
          { id: IAP_PRODUCT_YEARLY,  type: _P.ProductType.PAID_SUBSCRIPTION, platform: _platform },
        ]);

        // v13 event listeners MUST be attached before initialize().
        _store.when()
          .productUpdated((product) => {
            _log("productUpdated", product?.id, "state=", product?.state, "canPurchase=", product?.canPurchase, "offers=", product?.offers?.length);
            const summary = _summariseProduct(product);
            if (!summary) return; // product not yet ready (no price)
            if (product.id === IAP_PRODUCT_MONTHLY) _products.monthly = summary;
            if (product.id === IAP_PRODUCT_YEARLY)  _products.yearly  = summary;
            _emitProducts();
            if (_hasAnyProduct()) _setStatus({ phase: "ready", errorMessage: null });
          })
          .approved((transaction) => {
            _log("approved txn:", transaction?.transactionId, "product:", transaction?.products?.[0]?.id);
            // Correct Apple pattern: only call `transaction.finish()` after
            // the backend has confirmed the receipt. If the backend fails,
            // we intentionally DO NOT finish — the plugin will re-emit
            // `approved()` on the next app launch so we get a fresh chance
            // to verify. Finishing prematurely means a paid user could
            // end up without premium unlocked in our DB.
            _verifyReceiptWithBackend(transaction, _appUserId).then((ok) => {
              if (ok) {
                try { transaction.finish(); } catch (e) { _warn("finish() threw:", e); }
              } else {
                _warn("skipping finish() — backend verify failed, plugin will replay on next launch");
              }
            });
          })
          .verified((receipt) => {
            _log("verified receipt");
            _verifyReceiptWithBackend(receipt, _appUserId);
          });

        // Also surface StoreKit errors via BOTH the modern and legacy paths.
        try {
          if (typeof _store.error === "function") {
            _store.error((err) => _warn("store.error:", err?.code, err?.message, err));
          }
        } catch { /* older plugin versions */ }
        try {
          if (_store.when && typeof _store.when === "function") {
            const w = _store.when();
            if (typeof w.error === "function") {
              w.error((err) => _warn("when().error:", err?.code, err?.message, err));
            }
          }
        } catch { /* noop */ }
      }
      // end of `if (!_registered)` — register-once guard

      // -----------------------------------------------------------------
      // Bug 2 fix: canonical v13 initialize signature WITH `needAppReceipt`.
      // Google Play does NOT need `needAppReceipt` — it uses the raw
      // purchase token from BillingClient directly, which the plugin
      // extracts on the `approved()` callback.
      // -----------------------------------------------------------------
      const _platform = _nativeStorePlatform(_P);
      _log("initialize() platform=", _platform);
      if (_platform === _P.Platform.APPLE_APPSTORE) {
        await _store.initialize([
          { platform: _platform, options: { needAppReceipt: true } },
        ]);
      } else {
        await _store.initialize([
          { platform: _platform },
        ]);
      }
      _log("initialize() resolved. products registered:", (_store.products || []).map((p) => p.id));

      _setStatus({ phase: "loading", retryCount: 0 });

      // -----------------------------------------------------------------
      // Bug 4 fix: retry on app resume (iPad Stage Manager / split view).
      // -----------------------------------------------------------------
      _installResumeListener();

      // Kick off the (patient) product-fetch loop. Do NOT await it — return
      // to the caller so the UI can render the loading state immediately.
      _updateWithBackoff();
      return { ok: true };
    } catch (err) {
      _warn("init threw:", err?.message || err);
      _initing = null;
      _setStatus({ phase: "loading" });
      setTimeout(() => { initIAP(_appUserId); }, 4000);
      return { ok: false, error: err?.message || String(err), retrying: true };
    }
  })();
  return _initing;
}

let _pluginRetryCount = 0;
const PLUGIN_RETRY_MAX = 5;
let _resumeInstalled = false;
let _registered = false; // module-level guard so we register/attach listeners once
function _installResumeListener() {
  if (_resumeInstalled) return;
  _resumeInstalled = true;
  const onResume = () => {
    _log("app resumed / visible; refreshing products");
    if (!_hasAnyProduct() && _status.phase !== "ready") _updateWithBackoff();
    else if (_store && typeof _store.update === "function") {
      _store.update().catch(() => {});
    }
  };
  try {
    document.addEventListener("resume", onResume);
    document.addEventListener("visibilitychange", () => {
      if (typeof document !== "undefined" && !document.hidden) onResume();
    });
    // Capacitor App plugin (if available) — best-effort.
    import("@capacitor/app").then((mod) => {
      const App = mod?.App || mod?.default;
      if (App && typeof App.addListener === "function") {
        App.addListener("appStateChange", (state) => {
          if (state?.isActive) onResume();
        });
      }
    }).catch(() => { /* not installed on all platforms */ });
  } catch (e) { _warn("resume listener install failed:", e); }
}

export async function retryIAP() {
  if (!isIOSNative()) return { skipped: "not_ios_native" };
  _log("manual retry requested");
  if (!_store) {
    _initing = null;
    return initIAP(_appUserId);
  }
  _setStatus({ phase: "loading", errorMessage: null, retryCount: 0 });
  _updateWithBackoff();
  return { ok: true };
}

function _summariseProduct(p) {
  if (!p) return null;
  // v13 preferred path: p.getOffer() returns the "primary" offer. Fallbacks
  // cover older plugin releases and Apple StoreKit 1 products.
  const offer = (typeof p.getOffer === "function" ? p.getOffer() : null) || p.offers?.[0] || null;
  const phase = offer?.pricingPhases?.[0] || {};
  const priceString = phase.price || p.pricing?.price || "";
  // A product with no priceString means StoreKit hasn't returned pricing
  // yet — we MUST NOT expose it to the UI or the Buy button would be a no-op.
  if (!priceString) return null;
  return {
    id: p.id,
    title: p.title,
    description: p.description,
    priceString,
    priceMicros: phase.priceMicros ?? null,
    currency: phase.currency || p.pricing?.currency || "USD",
    period: phase.billingPeriod || (p.id.includes("monthly") ? "P1M" : "P1Y"),
    canPurchase: !!p.canPurchase,
    state: p.state || null,
    raw: p,
  };
}

async function _verifyReceiptWithBackend(txnOrReceipt, appUserId) {
  try {
    const t = txnOrReceipt || {};
    const native = t.nativePurchase || {};
    const isAndroid = isAndroidNative();

    // ---------------- Google Play ----------------
    // On Android, the plugin surfaces the raw purchase token via
    // `purchaseToken` or inside nativePurchase.purchaseToken. We forward
    // the token + product id + package name to the backend for
    // server-side verification against the Google Play Developer API.
    if (isAndroid) {
      const purchaseToken =
        t.purchaseToken ||
        t.transactionId ||
        native.purchaseToken ||
        (t.transaction && t.transaction.purchaseToken) ||
        null;
      const orderId =
        t.orderId ||
        native.orderId ||
        (t.transaction && t.transaction.orderId) ||
        null;
      const productId = t?.products?.[0]?.id || t.productId || t.product_id;
      const packageName =
        native.packageName ||
        (t.transaction && t.transaction.packageName) ||
        (typeof window !== "undefined" && window.Capacitor?.getApp?.().id) ||
        "com.ixnova.app";
      const body = {
        product_id: productId,
        purchase_token: purchaseToken,
        order_id: orderId,
        package_name: packageName,
        raw: {
          nativePurchase: native,
          transaction: t.transaction || null,
        },
        app_user_id: appUserId || null,
      };
      _log("verify → /billing/google/verify product:", productId, "hasToken:", !!purchaseToken);
      try {
        const res = await api.post("/billing/google/verify", body);
        const ok = !!(res?.data?.ok);
        _log("google verify ← ok=", ok, "premium=", res?.data?.premium);
        return ok;
      } catch (e) {
        _warn("google verify failed:", e?.response?.status);
        return false;
      }
    }

    // ---------------- Apple App Store ----------------
    const jws =
      t.jwsRepresentation ||
      t.signedTransaction ||
      t.signedPayload ||
      native.jwsRepresentation ||
      native.signedTransaction ||
      native.signedPayload ||
      (t.transaction && t.transaction.jwsRepresentation) ||
      null;
    const body = {
      product_id: t?.products?.[0]?.id || t.productId || t.product_id,
      transaction_id: t.transactionId || t.id,
      raw: {
        jwsRepresentation: jws,
        appStoreReceipt: t.appStoreReceipt || native.appStoreReceipt || null,
        receipt: t.receipt || native.receipt || null,
        nativePurchase: native,
        transaction: t.transaction || null,
      },
      app_user_id: appUserId || null,
    };
    _log("verify → /billing/apple/verify product:", body.product_id, "hasJWS:", !!jws);
    try {
      const res = await api.post("/billing/apple/verify", body);
      const ok = !!(res?.data?.ok);
      _log("apple verify ← ok=", ok, "premium=", res?.data?.premium);
      return ok;
    } catch (e) {
      _warn("apple verify failed:", e?.response?.status);
      return false;
    }
  } catch (e) { _warn("verify handler threw:", e); return false; }
}

/**
 * Trigger the native Apple purchase sheet. Uses the v13 preferred call
 * `store.order(product.getOffer())`.
 */
export async function purchaseIAP(which /* "monthly" | "yearly" */) {
  if (!_isNativeIAP()) throw new Error("purchase_unavailable");
  if (!_store) throw new Error("store_not_ready");
  const id = which === "yearly" ? IAP_PRODUCT_YEARLY : IAP_PRODUCT_MONTHLY;
  const product = _store.get(id);
  if (!product) {
    _warn("purchase requested but product not found:", id, "registered=", (_store.products || []).map((p) => p.id));
    throw new Error("product_not_found");
  }
  if (product.canPurchase === false) {
    _warn("product not purchasable:", id, "state=", product.state);
  }
  const offer = (typeof product.getOffer === "function" ? product.getOffer() : null) || product.offers?.[0];
  if (!offer) throw new Error("offer_not_found");
  _log("order() product:", id);
  // v13 preferred call. Falls back to offer.order() for older plugin builds.
  if (typeof _store.order === "function") {
    return _store.order(offer);
  }
  return offer.order();
}

export async function restoreIAP() {
  if (!isIOSNative()) return { skipped: "not_ios_native" };
  if (!_store) return { skipped: "store_not_ready" };
  try {
    _log("restorePurchases()");
    await _store.restorePurchases();
    return { ok: true };
  } catch (err) {
    _warn("restore failed:", err?.code, err?.message);
    return { ok: false, error: err?.message || String(err) };
  }
}

export const IAP_CONSTANTS = {
  MONTHLY_ID: IAP_PRODUCT_MONTHLY,
  YEARLY_ID:  IAP_PRODUCT_YEARLY,
  isReady: () => _status.phase === "ready",
};
